import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { FlexDirectionProps, HeightProps } from 'styled-system';
import { flexDirection, height, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { ListStyleProps } from 'common/theme/system-utilities';
import { listStyle } from 'common/theme/system-utilities';
import type { Breadcrumb as BaseBreadcrumb } from 'common/types/Breadcrumb';
import Arrow from './Arrow';
import Breadcrumb from './Breadcrumb';
import ListItem from './ListItem';
import { getBreadcrumbs } from './utils';

interface BreadcrumbsProps {
  /** Array of breadcrumbs. */
  items: BaseBreadcrumb[];
  /** Disable last breadcrumb */
  isLastInactive?: boolean;
  /** SSR property for breadcrumbs version (small or large) */
  matchesTabletViewport?: boolean;
  /** Display as back link with left arrow always visible */
  isBackLink?: boolean;
}

const gridItemStyles: ComponentProps<typeof GridItem> = {
  px: ['sp_0', 'sp_12', null, 'sp_16'],
  my: ['sp_12', 'sp_16', null, 'sp_20'],
};

const List = styled(Flex)<HeightProps & SpaceProps & ListStyleProps>`
  ${height};
  ${space};
  ${listStyle};
`;

const listStyles: ComponentProps<typeof List> = {
  as: 'ol',
  flexWrap: 'wrap',
  height: ['sp_20', null, null, 'sp_24'],
  m: 'sp_0',
  p: 'sp_0',
  listStyle: 'none',
};

const StyledListItem = styled(ListItem)<SpaceProps & FlexDirectionProps>`
  ${space};
  ${flexDirection};
`;

const StyledGrid = styled(Grid)`
  scroll-margin-top: 0px;
  scroll-behavior: smooth;
`;

const Breadcrumbs = ({
  matchesTabletViewport = false,
  isLastInactive,
  items,
  isBackLink = false,
}: BreadcrumbsProps) => {
  const { t } = useLocalization();
  const {
    matches: { matchesTablet },
  } = useMatchesViewport(matchesTabletViewport);
  const breadcrumbs = getBreadcrumbs({
    items,
    isLastInactive,
    matchesTablet: isBackLink ? false : matchesTablet,
  });

  return (
    <StyledGrid data-corgi-id="breadcrumbs" id="main-content">
      <GridItem as="nav" aria-label={t('h24_breadcrumbs_label')} {...gridItemStyles}>
        <List {...listStyles}>
          {breadcrumbs.map(({ name, url }, index) => {
            const isLastItem = index === breadcrumbs.length - 1;
            const isDisabled = isLastInactive && isLastItem && matchesTablet && !isBackLink;
            const isArrowNeeded = !isLastItem || !matchesTablet || isBackLink;

            return (
              <StyledListItem
                flexDirection={isBackLink ? 'row-reverse' : ['row-reverse', 'row']}
                key={name + index}
              >
                <Breadcrumb name={name} url={url} disabled={isDisabled} />
                {isArrowNeeded && <Arrow reversed={matchesTablet && !isBackLink} />}
              </StyledListItem>
            );
          })}
        </List>
      </GridItem>
    </StyledGrid>
  );
};

export default Breadcrumbs;
